import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { Carousel, CarouselFormData } from "../types/carousel";
import CarouselForm from "../components/CarouselForm";
import CarouselList from "../components/CarouselList";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { toast } from "sonner";

const CarouselPage: React.FC = () => {
  const [items, setItems] = useState<Carousel[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<Carousel | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    item: Carousel | null;
  }>({
    isOpen: false,
    item: null,
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("carousel")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error("Error fetching carousel items:", error);
      toast.error("Error fetching carousel items");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (formData: CarouselFormData) => {
    try {
      setLoading(true);

      // Upload media if provided
      let mediaUrl: string | null = null;
      if (formData.media) {
        const fileExt = formData.media.name.split(".").pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `carousel/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from("sceneon_image")
          .upload(filePath, formData.media);

        if (uploadError) {
          console.error("Upload error:", uploadError);
          toast.error("Error uploading media");
          throw uploadError;
        }

        const { data } = supabase.storage
          .from("sceneon_image")
          .getPublicUrl(filePath);

        mediaUrl = data.publicUrl;
      }

      const { data, error } = await supabase
        .from("carousel")
        .insert([
          {
            title: formData.title,
            description: formData.description || null,
            cta_text: formData.cta_text || null,
            preset_id_template: formData.preset_id_template,
            media_type: formData.media_type,
            media_url: mediaUrl,
            is_visible: formData.is_visible,
          },
        ])
        .select();

      if (error) throw error;

      setItems([data[0], ...items]);
      setShowForm(false);
      toast.success("Carousel item created successfully!");
    } catch (error) {
      console.error("Error creating carousel item:", error);
      toast.error("Error creating carousel item");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItem = async (formData: CarouselFormData) => {
    if (!editingItem?.id) return;

    try {
      setLoading(true);

      // Upload new media if provided
      let mediaUrl: string | null = editingItem.media_url || null;
      if (formData.media) {
        const fileExt = formData.media.name.split(".").pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `carousel/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from("sceneon_image")
          .upload(filePath, formData.media);

        if (uploadError) {
          console.error("Upload error:", uploadError);
          toast.error("Error uploading media");
          throw uploadError;
        }

        const { data } = supabase.storage
          .from("sceneon_image")
          .getPublicUrl(filePath);

        mediaUrl = data.publicUrl;
      }

      const { data, error } = await supabase
        .from("carousel")
        .update({
          title: formData.title,
          description: formData.description || null,
          cta_text: formData.cta_text || null,
          preset_id_template: formData.preset_id_template,
          media_type: formData.media_type,
          media_url: mediaUrl,
          is_visible: formData.is_visible,
        })
        .eq("id", editingItem.id)
        .select();

      if (error) throw error;

      setItems(
        items.map((item) => (item.id === editingItem.id ? data[0] : item))
      );
      setEditingItem(null);
      setShowForm(false);
      toast.success("Carousel item updated successfully!");
    } catch (error) {
      console.error("Error updating carousel item:", error);
      toast.error("Error updating carousel item");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (item: Carousel) => {
    setDeleteModal({ isOpen: true, item });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.item?.id) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from("carousel")
        .delete()
        .eq("id", deleteModal.item.id);

      if (error) throw error;

      setItems(items.filter((item) => item.id !== deleteModal.item?.id));
      setDeleteModal({ isOpen: false, item: null });
      toast.success("Carousel item deleted successfully!");
    } catch (error) {
      console.error("Error deleting carousel item:", error);
      toast.error("Error deleting carousel item");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, item: null });
  };

  const handleEdit = (item: Carousel) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Carousel</h1>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-berrys-orange hover:bg-berrys-orange-dark text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Add Carousel Item
          </button>
        )}
      </div>

      {showForm && (
        <CarouselForm
          onSubmit={editingItem ? handleUpdateItem : handleCreateItem}
          onCancel={handleCancel}
          loading={loading}
          initialData={editingItem}
        />
      )}

      {!showForm && items.length === 0 && !loading ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <svg
            className="mx-auto h-16 w-16 text-gray-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No carousel items found
          </h3>
          <p className="text-gray-500 mb-4">
            Get started by adding your first carousel item.
          </p>
        </div>
      ) : (
        !showForm && (
          <CarouselList
            items={items}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />
        )
      )}

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Carousel Item"
        message="Are you sure you want to delete this carousel item? This action cannot be undone."
        itemName={deleteModal.item?.title || "carousel item"}
        loading={loading}
      />
    </div>
  );
};

export default CarouselPage;
